import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, FileCheck2, Send, Play, RotateCcw, Lock, Hash } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { api, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Card } from "../components/Card";
import { Severity, StatusPill } from "../components/Severity";

const EvidenceForm = ({ taskId, onDone }) => {
  const { t } = useI18n();
  const [ev, setEv] = useState({ title: "", description: "", link: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post(`/tasks/${taskId}/evidence`, ev); setEv({ title: "", description: "", link: "" }); toast.success(t("saved")); onDone(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="evidence-form">
      <Input data-testid="evidence-title-input" placeholder={t("title")} value={ev.title} onChange={(e) => setEv({ ...ev, title: e.target.value })} className="rounded-xl" required />
      <Textarea data-testid="evidence-description-input" placeholder={t("description")} value={ev.description} onChange={(e) => setEv({ ...ev, description: e.target.value })} className="rounded-xl" rows={3} required />
      <Input data-testid="evidence-link-input" dir="ltr" placeholder="https://" value={ev.link} onChange={(e) => setEv({ ...ev, link: e.target.value })} className="rounded-xl" aria-label={t("link")} />
      <Button data-testid="evidence-submit-button" className="rounded-xl bg-slate-900 hover:bg-slate-800"><FileCheck2 className="h-4 w-4" />{t("addEvidence")}</Button>
    </form>
  );
};

export default function TaskDetail() {
  const { id } = useParams();
  const { t, tv } = useI18n();
  const [task, setTask] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get(`/tasks/${id}`).then((r) => setTask(r.data)).catch((e) => setErr(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn) => { try { await fn(); toast.success(t("saved")); load(); } catch (e) { toast.error(errMsg(e)); } };
  const setStatus = (s) => act(() => api.patch(`/tasks/${id}`, { status: s }));

  if (err) return <p data-testid="task-error" className="text-rose-700">{err}</p>;
  if (!task) return <p className="text-slate-500">{t("loading")}</p>;
  const locked = ["pending_approval", "approved", "closed"].includes(task.status);

  return (
    <div className="space-y-6">
      <Link to="/tasks" data-testid="back-to-tasks" className="inline-flex items-center gap-1 text-sm text-sky-700 hover:underline"><ArrowLeft className="h-4 w-4 rtl:rotate-180" />{t("backToTasks")}</Link>
      <Card>
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="font-mono text-xs text-sky-700">{task.id} · {task.use_case_id}</p>
            <h1 data-testid="page-title" className="mt-1 text-2xl font-bold tracking-tight text-slate-900">{task.title}</h1>
            <div className="mt-3 flex flex-wrap gap-2"><Severity value={task.priority} /><StatusPill value={task.status} testId="task-status" /><StatusPill value={tv(task.domain)} /></div>
            <p className="mt-3 text-sm text-slate-600">{t("assignee")}: <span className="font-medium">{task.assignee_name}</span> · {t("dueDate")}: <span className="font-mono">{task.due_date || "—"}</span></p>
          </div>
          <div className="flex flex-wrap gap-2">
            {task.status === "open" && <Button data-testid="task-start-button" onClick={() => setStatus("in_progress")} className="rounded-xl bg-sky-600 hover:bg-sky-700"><Play className="h-4 w-4" />{t("startWork")}</Button>}
            {["rejected", "evidence_submitted"].includes(task.status) && <Button data-testid="task-reopen-button" variant="outline" onClick={() => setStatus("in_progress")} className="rounded-xl"><RotateCcw className="h-4 w-4" />{t("reopen")}</Button>}
            {task.status === "evidence_submitted" && <Button data-testid="task-submit-approval-button" onClick={() => act(() => api.post(`/tasks/${id}/submit-approval`))} className="rounded-xl bg-slate-900 hover:bg-slate-800"><Send className="h-4 w-4 rtl:rotate-180" />{t("submitApproval")}</Button>}
            {task.status === "approved" && <Button data-testid="task-close-button" onClick={() => setStatus("closed")} className="rounded-xl bg-emerald-700 hover:bg-emerald-800">{t("closeTask")}</Button>}
          </div>
        </div>
        {task.use_case && <p className="mt-4 rounded-xl bg-slate-50 p-4 text-sm text-slate-700">{task.use_case.description}<br /><span className="mt-2 block text-xs text-slate-500">{t("acceptance")}: {task.use_case.acceptance_criteria}</span></p>}
      </Card>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900">{t("evidence")} ({task.evidence.length})</h2>
          <ul className="space-y-3" data-testid="evidence-list">
            {task.evidence.map((e) => (
              <li key={e.id} className="rounded-xl border border-slate-200 p-3 text-sm">
                <p className="font-semibold text-slate-900">{e.title}</p>
                <p className="mt-1 text-slate-600">{e.description}</p>
                {e.link && <a href={e.link} target="_blank" rel="noreferrer" className="mt-1 block truncate text-xs text-sky-700" dir="ltr">{e.link}</a>}
                <p className="mt-2 flex items-center gap-1 font-mono text-[11px] text-slate-500" dir="ltr"><Hash className="h-3 w-3" />{e.sha256.slice(0, 24)}… · {e.submitted_by_name}</p>
              </li>
            ))}
          </ul>
          <div className="mt-6">
            {locked ? <p className="flex items-center gap-2 text-sm text-slate-500"><Lock className="h-4 w-4" />{tv(task.status)}</p> : <EvidenceForm taskId={id} onDone={load} />}
          </div>
        </Card>
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-slate-900">{t("nav_approvals")}</h2>
          <ul className="space-y-3" data-testid="task-approvals-list">
            {task.approvals.length === 0 && <li className="text-sm text-slate-500">{t("noData")}</li>}
            {task.approvals.map((a) => (
              <li key={a.id} className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 p-3 text-sm">
                <div><p>{t("requestedBy")}: <span className="font-medium">{a.requested_by_name}</span></p>
                  {a.approver_name && <p className="text-slate-600">{t("approver")}: {a.approver_name}</p>}
                  {a.comment && <p className="mt-1 text-slate-500">“{a.comment}”</p>}</div>
                <StatusPill value={a.status} />
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}
